import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Send, CheckCircle, XCircle, Clock, FileCheck, Package, Upload, Image, Video,
  Mic, FileText, Trash2, Eye, MessageSquare, RefreshCw, Activity, DollarSign,
  CreditCard, Link2, ChevronDown, ChevronUp, Plus, AlertCircle, Shield,
  Bookmark, Copy, ArrowRight, Star, Calendar, Globe, Monitor, Lock, Edit3, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import API from '@/lib/api';
import { MediationBox } from '@/components/MediationBox';
import { EmbeddedCheckoutModal } from '@/components/EmbeddedCheckoutModal';
import { PaymentSetupButton } from '@/components/CardSetupWidget';

// ── Reusable content-asset uploader (image / video / audio / pdf via /uploads/asset) ──
function AssetUploader({ value, kind, onChange, label = 'Upload file' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fullUrl = value ? (value.startsWith('http') ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`) : '';

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await API.post('/uploads/asset', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(res.data.url, res.data.kind, res.data.filename);
      toast.success('File uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); };

  const KindIcon = { image: Image, video: Video, audio: Mic, document: FileText }[kind] || FileText;

  return (
    <div>
      <input ref={inputRef} type="file" className="hidden" data-testid="content-file-input"
        accept="image/*,video/*,audio/*,application/pdf"
        onChange={e => upload(e.target.files?.[0])} />
      {value ? (
        <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid="content-file-preview">
          {kind === 'image' ? (
            <img src={fullUrl} alt="preview" className="w-12 h-12 rounded object-cover border border-slate-200" />
          ) : (
            <div className="w-12 h-12 rounded bg-white border border-slate-200 flex items-center justify-center">
              <KindIcon className="w-5 h-5 text-slate-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium text-slate-700 capitalize">{kind || 'file'} attached</p>
            <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline truncate block">{fullUrl}</a>
          </div>
          <Button type="button" size="sm" variant="ghost" onClick={() => onChange('', '')} data-testid="content-file-remove"
            className="text-slate-400 hover:text-red-500 text-xs h-6">Remove</Button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          data-testid="content-upload-dropzone"
          className={`cursor-pointer rounded-lg border-2 border-dashed px-3 py-3 text-center transition-colors ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400 bg-white'}`}>
          {uploading ? (
            <span className="text-xs text-slate-500 inline-flex items-center gap-2">
              <span className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /> Uploading…
            </span>
          ) : (
            <span className="text-xs text-slate-500"><Upload className="w-3.5 h-3.5 inline mr-1.5 text-slate-400" />{label} — image, video, audio or PDF</span>
          )}
        </div>
      )}
    </div>
  );
}


// ── Shared helpers ──
function StatusBadge({ status }) {
  const map = {
    draft: 'bg-slate-50 text-slate-500 border-slate-200',
    none: 'bg-slate-50 text-slate-400 border-slate-200',
    sent: 'bg-blue-50 text-blue-600 border-blue-200',
    proposal_sent: 'bg-blue-50 text-blue-600 border-blue-200',
    accepted: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    active: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    rejected: 'bg-red-50 text-red-600 border-red-200',
    countered: 'bg-purple-50 text-purple-600 border-purple-200',
    pending: 'bg-amber-50 text-amber-600 border-amber-200',
    pending_review: 'bg-amber-50 text-amber-600 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    changes_requested: 'bg-orange-50 text-orange-600 border-orange-200',
    superseded: 'bg-slate-50 text-slate-400 border-slate-200',
    funded: 'bg-blue-50 text-blue-600 border-blue-200',
    paid: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    proposed: 'bg-amber-50 text-amber-600 border-amber-200',
  };
  return (
    <Badge variant="outline" className={`text-[10px] font-semibold capitalize ${map[status] || 'bg-slate-50 text-slate-400'}`}>
      {(status || 'none').replace(/_/g, ' ')}
    </Badge>
  );
}

function InfoCell({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-xs font-medium text-slate-700 mt-0.5">{value || '—'}</p>
    </div>
  );
}

function SectionToggle({ title, icon: Icon, open, onToggle, children, badge }) {
  return (
    <div className="border border-slate-100 rounded-xl bg-white overflow-hidden">
      <button onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-800">{title}</span>
          {badge}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-slate-50">{children}</div>}
    </div>
  );
}

const PLATS = ['Instagram', 'TikTok', 'YouTube', 'Twitter/X', 'Facebook', 'LinkedIn', 'Podcast', 'Website'];
const TERRS = ['US', 'UK', 'EU', 'Canada', 'APAC', 'LATAM', 'Middle East', 'Global'];
const DELIVERABLE_TYPES = ['image_ads', 'short_videos', 'long_form_video', 'stories', 'reels', 'voiceovers', 'promo_stills', 'podcast_spots', 'blog_content'];
const EXTRA_LIKENESS_OPTIONS = [
  { value: 'pronunciation_script', label: 'Pronunciation Script' },
  { value: 'movement_reference', label: 'Movement Reference' },
  { value: 'wardrobe_look_reference', label: 'Wardrobe / Look Reference' },
  { value: 'specific_styling_guidance', label: 'Specific Styling Guidance' },
  { value: 'additional_video_references', label: 'Additional Video References' },
  { value: 'additional_audio_references', label: 'Additional Audio References' },
  { value: 'campaign_specific_notes', label: 'Campaign-Specific Notes' },
];

function ChipSelect({ options, selected, onChange, label }) {
  return (
    <div>
      {label && <Label className="text-xs text-slate-600 mb-1.5 block">{label}</Label>}
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const lbl = typeof opt === 'string' ? opt.replace(/_/g, ' ') : opt.label;
          return (
            <button key={val} type="button"
              onClick={() => onChange(selected.includes(val) ? selected.filter(x => x !== val) : [...selected, val])}
              className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all capitalize
                ${selected.includes(val) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
              {lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ProposalTab moved to ProposalBuilder.js (Phase 1 refactor)
export { ProposalTab } from './ProposalBuilder';



// ═══════════════════════════════════════════════════════════
// ── 2. LICENSE TAB (Enhanced) ──
// ═══════════════════════════════════════════════════════════
export function LicenseTab({ deal, onRefresh }) {
  const [amendments, setAmendments] = useState([]);
  const [showAmendment, setShowAmendment] = useState(false);
  const [aForm, setAForm] = useState({ amendment_type: 'extension', description: '', changes: {}, additional_amount: '' });
  const [saving, setSaving] = useState(false);

  const loadAmendments = useCallback(async () => {
    try {
      const r = await API.get(`/buyer/deals/${deal.id}/amendments`);
      setAmendments(r.data.amendments);
    } catch {}
  }, [deal.id]);

  useEffect(() => { loadAmendments(); }, [loadAmendments]);

  const proposal = deal.proposal || {};
  const rt = proposal.rights_terms || {};
  const hasLicense = !!deal.license_id;
  const isAccepted = deal.proposal_status === 'accepted';

  const submitAmendment = async () => {
    setSaving(true);
    try {
      await API.post(`/buyer/deals/${deal.id}/amendments`, {
        amendment_type: aForm.amendment_type,
        description: aForm.description,
        changes: aForm.changes,
        additional_amount: parseFloat(aForm.additional_amount) || 0,
      });
      toast.success('Amendment proposed');
      setShowAmendment(false);
      setAForm({ amendment_type: 'extension', description: '', changes: {}, additional_amount: '' });
      loadAmendments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const respondAmendment = async (id, action) => {
    try {
      await API.put(`/buyer/deals/${deal.id}/amendments/${id}/respond`, { action });
      toast.success(`Amendment ${action}ed`);
      loadAmendments();
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Before acceptance: show proposed terms
  if (!isAccepted && !hasLicense) {
    return (
      <div data-testid="license-tab" className="space-y-3">
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <div className="flex items-center gap-3 mb-4">
            <FileCheck className="w-5 h-5 text-slate-400" />
            <h3 className="font-semibold text-slate-800">Proposed Rights Summary</h3>
            <StatusBadge status="pending" />
          </div>
          {rt.term_start ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm p-4 bg-slate-50 rounded-lg">
              <InfoCell label="License Type" value={rt.license_type} />
              <InfoCell label="Duration" value={rt.duration_type} />
              <InfoCell label="Term" value={`${rt.term_start} — ${rt.term_end}`} />
              <InfoCell label="Territory" value={(rt.territory || []).join(', ')} />
              <InfoCell label="Platforms" value={(rt.platforms || []).join(', ')} />
              <InfoCell label="Approval Required" value={rt.approval_required ? 'Yes' : 'No'} />
              <InfoCell label="Usage Scope" value={(rt.usage_scope || '').replace(/_/g, ' ')} />
              <InfoCell label="Output Reuse" value={rt.output_reuse ? 'Yes' : 'No'} />
              <InfoCell label="Endorsement" value={rt.endorsement_requested ? 'Requested' : 'No'} />
            </div>
          ) : (
            <p className="text-xs text-slate-400">No rights terms defined yet. Build a proposal first.</p>
          )}
          {rt.plain_language_summary && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-[10px] text-blue-500 uppercase tracking-wider mb-1">Plain-Language Summary</p>
              <p className="text-xs text-blue-800">{rt.plain_language_summary}</p>
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">Pending creator approval. License will be generated upon acceptance.</p>
        </div>
      </div>
    );
  }

  // After acceptance: finalized license + amendments
  return (
    <div data-testid="license-tab" className="space-y-3">
      <div className="bg-white rounded-xl border border-slate-100 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">License Agreement</h3>
            {deal.license_id && <code className="text-blue-600 text-xs font-mono bg-blue-50 px-2 py-0.5 rounded">{deal.license_id}</code>}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm p-4 bg-slate-50 rounded-lg">
          <InfoCell label="Status" value={deal.payment_status === 'paid' ? 'Active' : 'Pending Payment'} />
          <InfoCell label="Amount" value={`$${(proposal.commercial_offer?.amount || proposal.offered_price || 0).toLocaleString()}`} />
          <InfoCell label="License Type" value={rt.license_type} />
          <InfoCell label="Term" value={`${rt.term_start} — ${rt.term_end}`} />
          <InfoCell label="Territory" value={(rt.territory || []).join(', ')} />
          <InfoCell label="Platforms" value={(rt.platforms || []).join(', ')} />
          <InfoCell label="Usage Scope" value={(rt.usage_scope || '').replace(/_/g, ' ')} />
          <InfoCell label="Approval Required" value={rt.approval_required ? 'Yes' : 'No'} />
          <InfoCell label="Endorsement" value={rt.endorsement_requested ? 'Requested' : 'No'} />
        </div>
        {rt.restrictions && (
          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
            <p className="text-[10px] text-amber-500 uppercase tracking-wider mb-1">Restrictions</p>
            <p className="text-xs text-amber-800">{rt.restrictions}</p>
          </div>
        )}
        {rt.contract_url && (
          <a href={rt.contract_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mt-2 block">View Contract Document</a>
        )}
        <div className="flex gap-2 mt-4">
          {deal.license_id && (
            <Button variant="outline" size="sm" className="border-slate-200 text-xs h-7"
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/verify/${deal.license_id}`); toast.success('Verification link copied'); }}>
              <Link2 className="w-3 h-3 mr-1" />Copy Verify Link
            </Button>
          )}
        </div>
      </div>

      {/* Amendments */}
      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Amendments & Extensions</h4>
          <Button size="sm" variant="outline" onClick={() => setShowAmendment(true)} data-testid="propose-amendment-btn"
            className="text-xs h-7 border-slate-200">
            <Plus className="w-3 h-3 mr-1" />Propose Amendment
          </Button>
        </div>
        {amendments.length === 0 ? (
          <p className="text-xs text-slate-400">No amendments yet.</p>
        ) : (
          <div className="space-y-2">
            {amendments.map(a => (
              <div key={a.id} data-testid={`amendment-${a.id}`}
                className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] capitalize">{a.amendment_type?.replace(/_/g, ' ')}</Badge>
                    <StatusBadge status={a.status} />
                  </div>
                  {a.additional_amount > 0 && <span className="text-xs font-semibold text-slate-700">+${a.additional_amount.toLocaleString()}</span>}
                </div>
                <p className="text-xs text-slate-600">{a.description}</p>
                <p className="text-[10px] text-slate-400 mt-1">{new Date(a.proposed_at).toLocaleDateString()}</p>
                {a.status === 'proposed' && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={() => respondAmendment(a.id, 'accept')} className="bg-emerald-600 text-white text-xs h-6 px-2">Accept</Button>
                    <Button size="sm" variant="outline" onClick={() => respondAmendment(a.id, 'reject')} className="text-red-600 border-red-200 text-xs h-6 px-2">Reject</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Amendment Dialog */}
      <Dialog open={showAmendment} onOpenChange={setShowAmendment}>
        <DialogContent className="max-w-md" aria-describedby="amendment-desc">
          <DialogHeader><DialogTitle className="text-sm">Propose Amendment</DialogTitle></DialogHeader>
          <p id="amendment-desc" className="sr-only">Form to propose a deal amendment, extension, or add-on.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={aForm.amendment_type} onValueChange={v => setAForm(p => ({ ...p, amendment_type: v }))}>
                <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="extension">Extend License</SelectItem>
                  <SelectItem value="add_rights">Add Rights</SelectItem>
                  <SelectItem value="add_deliverables">Add Deliverables</SelectItem>
                  <SelectItem value="add_platforms">Add Platforms</SelectItem>
                  <SelectItem value="expand_territory">Expand Territory</SelectItem>
                  <SelectItem value="adjust_payment">Adjust Payment</SelectItem>
                  <SelectItem value="renew">Renew Agreement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={aForm.description} onChange={e => setAForm(p => ({ ...p, description: e.target.value }))}
                rows={2} className="text-xs mt-1" placeholder="Describe the amendment..." />
            </div>
            {aForm.amendment_type === 'extension' && (
              <div>
                <Label className="text-xs">New End Date</Label>
                <Input type="date" value={aForm.changes.new_term_end || ''} className="h-8 text-xs mt-1"
                  onChange={e => setAForm(p => ({ ...p, changes: { ...p.changes, new_term_end: e.target.value } }))} />
              </div>
            )}
            <div>
              <Label className="text-xs">Additional Amount ($)</Label>
              <Input type="number" value={aForm.additional_amount} className="h-8 text-xs mt-1" placeholder="0"
                onChange={e => setAForm(p => ({ ...p, additional_amount: e.target.value }))} />
            </div>
            <Button size="sm" className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={submitAmendment} disabled={saving}>
              {saving ? 'Proposing...' : 'Propose Amendment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 3. LIKENESS PACK TAB (Enhanced) ──
// ═══════════════════════════════════════════════════════════
export function LikenessPackTab({ deal, onRefresh }) {
  const [items, setItems] = useState(deal.likeness_pack || []);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ asset_type: 'headshot', label: '', url: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [likenessReq, setLikenessReq] = useState(null);

  useEffect(() => {
    API.get(`/buyer/deals/${deal.id}/likeness-request`).then(r => setLikenessReq(r.data)).catch(() => {});
    API.get(`/buyer/deals/${deal.id}/likeness-pack`).then(r => setItems(r.data.items || [])).catch(() => {});
  }, [deal.id]);

  const ASSET_TYPES = [
    { value: 'headshot', label: 'Headshot', icon: Image },
    { value: 'full_body', label: 'Full Body', icon: Image },
    { value: 'voice_sample', label: 'Voice Sample', icon: Mic },
    { value: 'video_clip', label: 'Video Clip', icon: Video },
    { value: 'document', label: 'Document', icon: FileText },
  ];

  const addItem = async () => {
    if (!form.url) { toast.error('URL is required'); return; }
    setSaving(true);
    try {
      const res = await API.post(`/buyer/deals/${deal.id}/likeness-pack`, form);
      setItems(prev => [res.data.item, ...prev]);
      setShowAdd(false);
      setForm({ asset_type: 'headshot', label: '', url: '', notes: '' });
      toast.success('Asset added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteItem = async (itemId) => {
    try {
      await API.delete(`/buyer/deals/${deal.id}/likeness-pack/${itemId}`);
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success('Asset removed');
    } catch { toast.error('Failed to remove'); }
  };

  const getAssetIcon = (type) => {
    const map = { headshot: Image, full_body: Image, voice_sample: Mic, video_clip: Video, document: FileText };
    const Icon = map[type] || Package;
    return <Icon className="w-4 h-4" />;
  };

  const lr = likenessReq?.likeness_request || {};
  const standardPack = likenessReq?.standard_pack || [];

  return (
    <div data-testid="likeness-pack-tab" className="space-y-4">
      {/* A. Standard Pack (Informational) */}
      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-blue-500" />
          <h3 className="text-sm font-semibold text-slate-800">Standard Likeness Pack</h3>
          <Badge className="bg-blue-50 text-blue-700 text-[9px]">Included by default</Badge>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {(standardPack.length > 0 ? standardPack : [
            { category: 'images', item: 'Clear front-facing headshots' },
            { category: 'images', item: 'Side / profile images' },
            { category: 'images', item: 'Smiling and neutral expressions' },
            { category: 'images', item: 'Full-body images' },
            { category: 'audio', item: 'Short clean audio clip' },
            { category: 'video', item: 'Short talking video' },
            { category: 'notes', item: 'Basic creator usage notes' },
          ]).map((sp, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs text-slate-600">
              <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
              {sp.item}
            </div>
          ))}
        </div>
      </div>

      {/* B. Additional Requirements */}
      {lr.additional_requirements?.length > 0 && (
        <div className="bg-white rounded-xl border border-amber-100 p-4">
          <h4 className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">Additional Requirements</h4>
          <div className="flex flex-wrap gap-1.5">
            {lr.additional_requirements.map((req, i) => (
              <Badge key={i} variant="outline" className="text-[10px] capitalize border-amber-200 text-amber-700">{req.replace(/_/g, ' ')}</Badge>
            ))}
          </div>
          {lr.custom_notes && <p className="text-xs text-slate-500 mt-2">{lr.custom_notes}</p>}
        </div>
      )}

      {/* C. Submitted Pack (Creator-submitted assets) */}
      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Submitted Pack</h3>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400">{items.length} assets</span>
            <Button data-testid="add-likeness-btn" size="sm" variant="outline" onClick={() => setShowAdd(true)} className="text-xs h-7 border-slate-200">
              <Upload className="w-3 h-3 mr-1" />Add Asset
            </Button>
          </div>
        </div>

        {showAdd && (
          <div className="p-3 bg-slate-50 rounded-lg mb-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.asset_type} onValueChange={v => setForm(f => ({ ...f, asset_type: v }))}>
                <SelectTrigger className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{ASSET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="Label" className="h-8 text-xs border-slate-200" />
            </div>
            <Input data-testid="likeness-url-input" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              placeholder="Asset URL *" className="h-8 text-xs border-slate-200" />
            <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Notes (optional)" className="h-8 text-xs border-slate-200" />
            <div className="flex gap-2">
              <Button data-testid="save-likeness-btn" size="sm" onClick={addItem} disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7">{saving ? 'Adding...' : 'Add'}</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-xs h-7 text-slate-400">Cancel</Button>
            </div>
          </div>
        )}

        {items.length === 0 && !showAdd ? (
          <div className="text-center py-8">
            <Package className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-xs text-slate-400">No assets submitted yet</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-2">
            {items.map(item => (
              <div key={item.id} data-testid={`likeness-item-${item.id}`}
                className="p-3 bg-slate-50 rounded-lg flex items-start gap-2">
                <div className="w-8 h-8 rounded bg-white flex items-center justify-center flex-shrink-0 text-slate-400 border border-slate-100">
                  {getAssetIcon(item.asset_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-700 truncate">{item.label}</p>
                  <Badge variant="outline" className="text-[9px] border-slate-200 capitalize mt-0.5">{item.asset_type?.replace(/_/g, ' ')}</Badge>
                  {item.notes && <p className="text-[10px] text-slate-400 mt-0.5">{item.notes}</p>}
                  {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline block truncate">{item.url}</a>}
                </div>
                <button onClick={() => deleteItem(item.id)} data-testid={`delete-likeness-${item.id}`}
                  className="text-slate-300 hover:text-red-500 transition-colors p-0.5">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 4. CONTENT REVIEW TAB (Enhanced) ──
// ═══════════════════════════════════════════════════════════
export function ContentTab({ deal, onRefresh }) {
  const [submissions, setSubmissions] = useState(deal.submissions || []);
  const [showSubmit, setShowSubmit] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', content_url: '', upload_kind: '', content_type: 'image', proposed_price: '', intended_usage: '' });
  const [saving, setSaving] = useState(false);
  const [feedbackText, setFeedbackText] = useState({});
  const [showFeedbackFor, setShowFeedbackFor] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [payingFor, setPayingFor] = useState(null);
  const [resubmitFor, setResubmitFor] = useState(null);
  const [reForm, setReForm] = useState({ title: '', content_url: '', upload_kind: '', content_type: 'image', description: '' });
  const [checkout, setCheckout] = useState(null);

  const loadSubmissions = useCallback(async () => {
    try {
      const res = await API.get(`/buyer/deals/${deal.id}/submissions`);
      setSubmissions(res.data.submissions);
    } catch { /* ignore */ }
  }, [deal.id]);

  const campaign = deal.campaign || {};
  const pricingMethod = campaign.pricing_method || 'fixed_per_submission';

  const submit = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    if (!form.content_url) { toast.error('Upload a file or paste a content URL'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.upload_kind;
      if (payload.proposed_price) payload.proposed_price = parseFloat(payload.proposed_price);
      else delete payload.proposed_price;
      const res = await API.post(`/buyer/deals/${deal.id}/submissions`, payload);
      setSubmissions(prev => [res.data.submission, ...prev]);
      setShowSubmit(false);
      setForm({ title: '', description: '', content_url: '', upload_kind: '', content_type: 'image', proposed_price: '', intended_usage: '' });
      toast.success('Content submitted for review');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const review = async (subId, action) => {
    const feedback = feedbackText[subId] || '';
    try {
      await API.put(`/buyer/deals/${deal.id}/submissions/${subId}/review`, { action, feedback });
      toast.success(action === 'approve' ? 'Content approved' : 'Changes requested');
      const res = await API.get(`/buyer/deals/${deal.id}/submissions`);
      setSubmissions(res.data.submissions);
      setShowFeedbackFor(null);
      setFeedbackText(prev => ({ ...prev, [subId]: '' }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openResubmit = (sub) => {
    setResubmitFor(sub.id);
    setReForm({ title: sub.title || '', content_url: '', upload_kind: '', content_type: sub.content_type || 'image', description: 'Revision of previous submission' });
  };

  const doResubmit = async (subId) => {
    if (!reForm.title) { toast.error('Title is required'); return; }
    if (!reForm.content_url) { toast.error('Upload a file or paste a URL'); return; }
    try {
      const res = await API.post(`/buyer/deals/${deal.id}/submissions/${subId}/resubmit`, {
        title: reForm.title, content_url: reForm.content_url, content_type: reForm.content_type,
        description: reForm.description || 'Revision of previous submission',
      });
      setSubmissions(prev => [res.data.submission, ...prev.map(s => s.id === subId ? { ...s, status: 'superseded' } : s)]);
      setResubmitFor(null);
      toast.success('Content resubmitted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const payForSubmission = async (subId) => {
    setPayingFor(subId);
    try {
      const res = await API.post(`/payments/checkout/submission/${subId}`, {});
      if (res.data.client_secret) {
        setCheckout({ clientSecret: res.data.client_secret, sessionId: res.data.session_id, title: 'Pay for content' });
      } else {
        toast.error('Failed to start payment');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed');
    } finally {
      setPayingFor(null);
    }
  };

  const onSubmissionPaid = async () => {
    const sid = checkout?.sessionId;
    setCheckout(null);
    if (!sid) return;
    try {
      const res = await API.get(`/payments/checkout/status/${sid}`);
      if (res.data.payment_status === 'paid' || res.data.already_processed) {
        toast.success('Payment confirmed!');
        loadSubmissions();
        if (onRefresh) onRefresh();
      }
    } catch { /* ignore */ }
  };

  const getTypeIcon = (type) => {
    const map = { image: Image, video: Video, short_video: Video, long_video: Video, audio: Mic, voice: Mic, document: FileText, ai_avatar: Zap };
    const Icon = map[type] || FileText;
    return <Icon className="w-3.5 h-3.5" />;
  };

  const scope = deal.content_scope || {};
  const proposal = deal.proposal || {};
  const briefDeliverables = proposal.campaign_brief?.deliverable_types || [];

  // Filter submissions
  const filtered = submissions.filter(s => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') return s.status === 'pending_review';
    if (statusFilter === 'approved') return s.status === 'approved';
    if (statusFilter === 'payment_required') return s.payment_status === 'payment_required';
    if (statusFilter === 'licensed') return s.license_status === 'active';
    if (statusFilter === 'changes') return s.status === 'changes_requested';
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (a.status === 'pending_review' && b.status !== 'pending_review') return -1;
    if (b.status === 'pending_review' && a.status !== 'pending_review') return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const counts = {
    total: submissions.length,
    pending: submissions.filter(s => s.status === 'pending_review').length,
    approved: submissions.filter(s => s.status === 'approved').length,
    paymentRequired: submissions.filter(s => s.payment_status === 'payment_required').length,
    licensed: submissions.filter(s => s.license_status === 'active').length,
  };

  return (
    <div data-testid="content-tab" className="space-y-4">
      {/* A. Intended Content Scope */}
      {(briefDeliverables.length > 0 || scope.description) && (
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <h4 className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">Intended Content Scope</h4>
          {briefDeliverables.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {briefDeliverables.map((dt, i) => (
                <Badge key={i} variant="outline" className="text-[10px] capitalize border-slate-200">{dt.replace(/_/g, ' ')}</Badge>
              ))}
            </div>
          )}
          {scope.description && <p className="text-xs text-slate-500">{scope.description}</p>}
        </div>
      )}

      {/* B. Header + Filters */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800 text-sm">Content Submissions</h3>
          <p className="text-[10px] text-slate-400 mt-0.5">
            {counts.pending} pending &middot; {counts.approved} approved &middot; {counts.paymentRequired} awaiting payment &middot; {counts.licensed} licensed
          </p>
        </div>
        <Button data-testid="submit-content-btn" size="sm" onClick={() => setShowSubmit(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7">
          <Upload className="w-3 h-3 mr-1" />Submit Content
        </Button>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5 overflow-x-auto">
        {[
          { id: 'all', label: `All (${counts.total})` },
          { id: 'pending', label: `Pending (${counts.pending})` },
          { id: 'approved', label: `Approved (${counts.approved})` },
          { id: 'payment_required', label: `Payment Required (${counts.paymentRequired})` },
          { id: 'licensed', label: `Licensed (${counts.licensed})` },
        ].map(f => (
          <button key={f.id} onClick={() => setStatusFilter(f.id)}
            className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all whitespace-nowrap
              ${statusFilter === f.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Submit form */}
      {showSubmit && (
        <div className="bg-white rounded-xl border border-blue-100 p-4 space-y-3">
          <Input data-testid="content-title-input" placeholder="Content title" value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="h-8 text-xs border-slate-200" />
          <Textarea placeholder="Description..." value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="text-xs border-slate-200" />
          <AssetUploader
            value={form.content_url}
            kind={form.upload_kind}
            onChange={(url, kind) => setForm(f => ({
              ...f,
              content_url: url,
              upload_kind: kind || '',
              content_type: ['image', 'video', 'audio', 'document'].includes(kind) ? kind : f.content_type,
            }))}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="content-url-input" placeholder="…or paste a content URL" value={form.content_url}
              onChange={e => setForm(f => ({ ...f, content_url: e.target.value }))} className="h-8 text-xs border-slate-200" />
            <Select value={form.content_type} onValueChange={v => setForm(f => ({ ...f, content_type: v }))}>
              <SelectTrigger className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="short_video">Short Video</SelectItem>
                <SelectItem value="long_video">Long Video</SelectItem>
                <SelectItem value="audio">Audio</SelectItem>
                <SelectItem value="voice">Voice</SelectItem>
                <SelectItem value="ai_avatar">AI Avatar</SelectItem>
                <SelectItem value="document">Document</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Price (optional)" type="number" step="0.01" value={form.proposed_price}
              onChange={e => setForm(f => ({ ...f, proposed_price: e.target.value }))} className="h-8 text-xs border-slate-200" />
            <Input placeholder="Intended usage (optional)" value={form.intended_usage}
              onChange={e => setForm(f => ({ ...f, intended_usage: e.target.value }))} className="h-8 text-xs border-slate-200" />
          </div>
          <div className="flex gap-2">
            <Button data-testid="save-content-btn" onClick={submit} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7">{saving ? 'Submitting...' : 'Submit'}</Button>
            <Button variant="ghost" onClick={() => setShowSubmit(false)} className="text-xs h-7 text-slate-400">Cancel</Button>
          </div>
        </div>
      )}

      {/* Submissions list */}
      {sorted.length === 0 && !showSubmit ? (
        <div className="text-center py-8 bg-white rounded-xl border border-slate-100">
          <Eye className="w-8 h-8 text-slate-200 mx-auto mb-2" />
          <p className="text-xs text-slate-400">{statusFilter === 'all' ? 'No content submissions yet' : 'No submissions match this filter'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map(sub => (
            <div key={sub.id} data-testid={`submission-${sub.id}`}
              className={`bg-white rounded-xl border p-3 ${sub.status === 'pending_review' ? 'border-amber-200' : sub.payment_status === 'payment_required' ? 'border-emerald-200' : 'border-slate-100'}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="text-slate-400">{getTypeIcon(sub.content_type)}</div>
                  <h4 className="font-medium text-slate-700 text-xs">{sub.title}</h4>
                  {sub.version > 1 && <Badge variant="outline" className="text-[9px] border-blue-200 text-blue-600">v{sub.version}</Badge>}
                  {sub.content_type && <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-400 capitalize">{sub.content_type.replace(/_/g, ' ')}</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={sub.status} />
                  {sub.payment_status === 'payment_required' && <Badge className="bg-emerald-50 text-emerald-700 text-[9px] border border-emerald-200">Payment Required</Badge>}
                  {sub.payment_status === 'paid' && <Badge className="bg-blue-50 text-blue-700 text-[9px] border border-blue-200">Paid</Badge>}
                  {sub.license_status === 'active' && <Badge className="bg-violet-50 text-violet-700 text-[9px] border border-violet-200">Licensed</Badge>}
                  {(sub.proposed_price || sub.confirmed_price) && (
                    <span className="text-xs font-semibold text-slate-700">${(sub.confirmed_price || sub.proposed_price)?.toLocaleString()}</span>
                  )}
                  <span className="text-[10px] text-slate-300">{new Date(sub.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              {sub.description && <p className="text-[10px] text-slate-400 mb-1">{sub.description}</p>}
              {sub.content_url && (sub.content_type === 'image' || sub.content_type === 'ai_avatar') && (
                <img src={sub.content_url} alt={sub.title} className="rounded border border-slate-200 max-h-32 w-auto object-contain mb-1 bg-white" />
              )}
              {sub.content_url && <a href={sub.content_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline block truncate">{sub.content_url}</a>}

              {/* Review actions */}
              {sub.status === 'pending_review' && (
                <div className="mt-2 pt-2 border-t border-slate-100 space-y-2">
                  {showFeedbackFor === sub.id ? (
                    <div className="space-y-2">
                      <Textarea data-testid={`feedback-input-${sub.id}`} placeholder="Feedback..."
                        value={feedbackText[sub.id] || ''} onChange={e => setFeedbackText(p => ({ ...p, [sub.id]: e.target.value }))}
                        rows={2} className="text-xs border-slate-200" />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => review(sub.id, 'approve')} className="bg-emerald-600 text-white text-xs h-6"><CheckCircle className="w-3 h-3 mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => review(sub.id, 'request_changes')} className="text-orange-600 border-orange-200 text-xs h-6">Request Changes</Button>
                        <Button size="sm" variant="ghost" onClick={() => setShowFeedbackFor(null)} className="text-slate-400 text-xs h-6">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => setShowFeedbackFor(sub.id)} className="bg-blue-600 text-white text-xs h-6"><MessageSquare className="w-3 h-3 mr-1" />Review</Button>
                      <Button size="sm" onClick={() => review(sub.id, 'approve')} className="bg-emerald-600 text-white text-xs h-6"><CheckCircle className="w-3 h-3 mr-1" />Approve</Button>
                    </div>
                  )}
                </div>
              )}

              {/* Pay for submission */}
              {sub.payment_status === 'payment_required' && (
                <div className="mt-2 pt-2 border-t border-emerald-100">
                  <Button size="sm" onClick={() => payForSubmission(sub.id)} disabled={payingFor === sub.id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-6">
                    <CreditCard className="w-3 h-3 mr-1" />{payingFor === sub.id ? 'Redirecting...' : `Pay $${(sub.confirmed_price || sub.proposed_price)?.toLocaleString()}`}
                  </Button>
                </div>
              )}

              {/* Resubmit */}
              {sub.status === 'changes_requested' && (
                <div className="mt-2 pt-2 border-t border-orange-100">
                  {resubmitFor === sub.id ? (
                    <div className="space-y-2" data-testid={`resubmit-form-${sub.id}`}>
                      <Input data-testid={`resubmit-title-${sub.id}`} placeholder="Title" value={reForm.title}
                        onChange={e => setReForm(f => ({ ...f, title: e.target.value }))} className="h-8 text-xs border-slate-200" />
                      <AssetUploader
                        value={reForm.content_url}
                        kind={reForm.upload_kind}
                        label="Upload revised file"
                        onChange={(url, kind) => setReForm(f => ({
                          ...f,
                          content_url: url,
                          upload_kind: kind || '',
                          content_type: ['image', 'video', 'audio', 'document'].includes(kind) ? kind : f.content_type,
                        }))}
                      />
                      <Input data-testid={`resubmit-url-${sub.id}`} placeholder="…or paste a URL" value={reForm.content_url}
                        onChange={e => setReForm(f => ({ ...f, content_url: e.target.value }))} className="h-8 text-xs border-slate-200" />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => doResubmit(sub.id)} data-testid={`resubmit-save-${sub.id}`}
                          className="bg-blue-600 text-white text-xs h-6"><RefreshCw className="w-3 h-3 mr-1" />Resubmit</Button>
                        <Button size="sm" variant="ghost" onClick={() => setResubmitFor(null)} className="text-slate-400 text-xs h-6">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => openResubmit(sub)} data-testid={`resubmit-btn-${sub.id}`}
                      className="text-blue-600 border-blue-200 text-xs h-6">
                      <RefreshCw className="w-3 h-3 mr-1" />Resubmit
                    </Button>
                  )}
                </div>
              )}

              {/* Feedback history */}
              {sub.feedback?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                  {sub.feedback.map((fb, i) => (
                    <div key={i} className={`text-[11px] p-1.5 rounded ${fb.action === 'approve' ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}`}>
                      <span className="font-medium capitalize">{fb.action?.replace(/_/g, ' ')}</span>
                      {fb.feedback && <span className="text-slate-600"> — {fb.feedback}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <EmbeddedCheckoutModal
        open={!!checkout}
        clientSecret={checkout?.clientSecret}
        title={checkout?.title}
        onComplete={onSubmissionPaid}
        onClose={() => setCheckout(null)}
      />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 5. PAYMENTS TAB (Enhanced — On-Platform) ──
// ═══════════════════════════════════════════════════════════
export function PaymentTab({ deal, onRefresh }) {
  const [payData, setPayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [fuForm, setFuForm] = useState({ amount: '', reason: 'extension', description: '' });
  const [fuSaving, setFuSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedPM, setSelectedPM] = useState(null);

  useEffect(() => {
    API.get(`/buyer/deals/${deal.id}/payments`).then(r => setPayData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // Load saved payment methods
    API.get('/billing/agency/payment-methods').then(r => {
      setSavedCards(r.data.payment_methods || []);
      const def = r.data.payment_methods?.find(p => p.is_default);
      if (def) setSelectedPM(def.id);
    }).catch(() => {});
  }, [deal.id]);

  const payDealDirect = async () => {
    setPaying(true);
    try {
      const res = await API.post(`/billing/agency/charge-deal/${deal.id}`, {
        payment_method_id: selectedPM || undefined,
      });
      if (res.data.funded) {
        toast.success('Payment confirmed! Funds secured.');
        setConfirmOpen(false);
        onRefresh();
        const r = await API.get(`/buyer/deals/${deal.id}/payments`);
        setPayData(r.data);
      } else {
        toast.info('Payment processing. Please refresh in a moment.');
        setConfirmOpen(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed. Please check your payment method.');
    } finally {
      setPaying(false);
    }
  };

  const submitFollowUp = async () => {
    if (!fuForm.amount || parseFloat(fuForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!selectedPM && savedCards.length > 0) {
      toast.error('Please select a payment method');
      return;
    }
    setFuSaving(true);
    try {
      const res = await API.post(`/billing/agency/charge-deal/${deal.id}/follow-up`, {
        amount: parseFloat(fuForm.amount),
        reason: fuForm.reason,
        description: fuForm.description,
        payment_method_id: selectedPM || undefined,
      });
      if (res.data.funded) {
        toast.success('Follow-up payment confirmed!');
        setShowFollowUp(false);
        onRefresh();
        const r = await API.get(`/buyer/deals/${deal.id}/payments`);
        setPayData(r.data);
      } else {
        toast.info('Payment processing...');
        setShowFollowUp(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setFuSaving(false);
    }
  };

  if (loading) return <div className="animate-pulse space-y-3"><div className="h-24 bg-slate-100 rounded-xl" /><div className="h-32 bg-slate-100 rounded-xl" /></div>;

  const os = payData?.offer_summary || {};
  const txns = payData?.transactions || [];
  const pm = payData?.payment_method;
  const dealStatus = payData?.deal_payment_status || deal.payment_status;
  const proposalAccepted = deal.proposal_status === 'accepted';
  const canPay = proposalAccepted && (dealStatus === 'none' || dealStatus === 'pending');

  const STATUS_LABELS = {
    awaiting_proposal: { label: 'Awaiting Creator', color: 'text-slate-600 bg-slate-100', desc: "The creator hasn't accepted the proposal yet. You'll be able to pay once they do." },
    funds_secured: { label: 'Funds Secured', color: 'text-blue-700 bg-blue-50', desc: 'Payment processed. Funds held pending creator approval.' },
    paid_out: { label: 'Paid Out', color: 'text-emerald-700 bg-emerald-50', desc: 'Payout released to creator.' },
    none: { label: 'Ready for Payment', color: 'text-amber-700 bg-amber-50', desc: 'Proposal accepted. Ready for payment.' },
    pending: { label: 'Ready for Payment', color: 'text-amber-700 bg-amber-50', desc: 'Proposal accepted. Ready for payment.' },
    funded: { label: 'Funded', color: 'text-blue-700 bg-blue-50', desc: 'Funds secured on platform.' },
    paid: { label: 'Paid', color: 'text-emerald-700 bg-emerald-50', desc: 'Payment completed.' },
  };
  // If the proposal hasn't been accepted yet, the payment-tab status must reflect
  // that (not imply the deal is ready to pay).
  const effectiveStatus = !proposalAccepted && (dealStatus === 'none' || dealStatus === 'pending')
    ? 'awaiting_proposal'
    : dealStatus;
  const statusInfo = STATUS_LABELS[effectiveStatus] || STATUS_LABELS.none;

  return (
    <div data-testid="payment-tab" className="space-y-3">
      {/* A. Offer Summary */}
      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <h4 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Offer Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <InfoCell label="Amount" value={`$${(os.amount || 0).toLocaleString()}`} />
          <InfoCell label="Payment Model" value={(os.payment_model || 'one_off').replace(/_/g, ' ')} />
          <InfoCell label="Approval Required" value={os.approval_required ? 'Yes — Payout held' : 'No — Payout on pay'} />
          <InfoCell label="Status" value={<span className={`text-xs font-medium px-2 py-0.5 rounded ${statusInfo.color}`}>{statusInfo.label}</span>} />
        </div>
        {statusInfo.desc && <p className="text-[10px] text-slate-400 mt-2">{statusInfo.desc}</p>}
      </div>

      {/* B. Payment Action */}
      {canPay && (
        <div className="bg-white rounded-xl border border-amber-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-slate-800">Process Payment</h4>
              <p className="text-xs text-slate-400 mt-0.5">
                {savedCards.length > 0
                  ? `Charge your saved card${os.approval_required ? ' — Funds will be held pending creator approval' : ''}`
                  : 'Add a payment method in Settings first'}
              </p>
            </div>
            <Button data-testid="pay-now-btn" onClick={() => setConfirmOpen(true)} disabled={paying || savedCards.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 px-6">
              <CreditCard className="w-3.5 h-3.5 mr-1.5" />Pay Now
            </Button>
          </div>
          {savedCards.length === 0 && (
            <div className="mt-3 flex items-center gap-3">
              <p className="text-[11px] text-amber-600">No payment method on file.</p>
              <PaymentSetupButton buttonLabel="Add payment method" className="h-8 text-xs" />
            </div>
          )}
        </div>
      )}

      {/* Payment confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm" data-testid="pay-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Confirm Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-slate-900">${(os.amount || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">{os.currency || 'USD'}</p>
            </div>
            {savedCards.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 mb-2">Payment method</p>
                <div className="space-y-1.5">
                  {savedCards.map(card => (
                    <button key={card.id} onClick={() => setSelectedPM(card.id)}
                      data-testid={`pm-option-${card.last4}`}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                        selectedPM === card.id ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                      <CreditCard className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-700">{card.label || `${card.brand || card.type} ending in ${card.last4}`}</span>
                      {card.is_default && <span className="text-[10px] text-slate-400 ml-auto">Default</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {os.approval_required && (
              <p className="text-[11px] text-slate-500 bg-blue-50 p-2 rounded-lg">Funds will be held until the creator approves the generated content.</p>
            )}
            <Button data-testid="confirm-pay-btn" onClick={payDealDirect} disabled={paying || !selectedPM}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-10">
              {paying ? 'Processing...' : `Confirm & Pay $${(os.amount || 0).toLocaleString()}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* No proposal accepted yet */}
      {!proposalAccepted && txns.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-4 text-center py-8" data-testid="payment-awaiting-acceptance">
          <DollarSign className="w-8 h-8 text-slate-200 mx-auto mb-2" />
          <p className="text-xs text-slate-400">
            Payment will be available once <strong className="text-slate-500">{deal.creator_name || 'the creator'}</strong> accepts your proposal.
          </p>
        </div>
      )}

      {/* C. Transaction Status / D. Follow-up & E. History */}
      {txns.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Payment History</h4>
            {(dealStatus === 'funded' || dealStatus === 'paid') && (
              <Button size="sm" variant="outline" onClick={() => setShowFollowUp(true)} data-testid="follow-up-btn"
                className="text-xs h-7 border-slate-200">
                <Plus className="w-3 h-3 mr-1" />Follow-Up Payment
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {txns.map(t => (
              <div key={t.id} data-testid={`payment-txn-${t.id}`}
                className="p-3 bg-slate-50 rounded-lg flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.funding_status === 'paid_out' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                  <DollarSign className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-800">${t.amount?.toLocaleString()}</span>
                    <Badge variant="outline" className={`text-[9px] capitalize ${t.funding_status === 'paid_out' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                      {t.funding_status?.replace(/_/g, ' ')}
                    </Badge>
                    {t.payment_type && t.payment_type !== 'initial' && (
                      <Badge variant="outline" className="text-[9px] capitalize text-violet-600 border-violet-200">{t.payment_type.replace(/_/g, ' ')}</Badge>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Fee: ${t.platform_fee?.toLocaleString()} &middot; Net: ${t.net_creator_payout?.toLocaleString()} &middot; {t.payment_method_summary}
                  </p>
                </div>
                <span className="text-[10px] text-slate-400 whitespace-nowrap">{t.funded_at ? new Date(t.funded_at).toLocaleDateString() : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mediation — agency refund request / dispute thread */}
      {txns.length > 0 && (
        <MediationBox
          dealId={deal.id}
          role="agency"
          fundsHeld={txns.some(t => ['funds_secured', 'in_review', 'awaiting_creator_approval'].includes(t.funding_status))}
          onChange={onRefresh}
        />
      )}

      {/* Follow-up Dialog */}
      <Dialog open={showFollowUp} onOpenChange={setShowFollowUp}>
        <DialogContent className="max-w-sm" aria-describedby="followup-desc">
          <DialogHeader><DialogTitle className="text-sm">Follow-Up Payment</DialogTitle></DialogHeader>
          <p id="followup-desc" className="sr-only">Add a follow-up payment for extensions, add-ons, or renewals.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={fuForm.reason} onValueChange={v => setFuForm(p => ({ ...p, reason: v }))}>
                <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="extension">Extension Fee</SelectItem>
                  <SelectItem value="addon">Add-On Fee</SelectItem>
                  <SelectItem value="renewal">Renewal Fee</SelectItem>
                  <SelectItem value="revised_offer">Revised Offer</SelectItem>
                  <SelectItem value="adjustment">Payment Adjustment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount ($)</Label>
              <Input type="number" value={fuForm.amount} onChange={e => setFuForm(p => ({ ...p, amount: e.target.value }))}
                className="h-8 text-xs mt-1" placeholder="1000" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={fuForm.description} onChange={e => setFuForm(p => ({ ...p, description: e.target.value }))}
                rows={2} className="text-xs mt-1" placeholder="Reason for follow-up..." />
            </div>
            <Button size="sm" className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={submitFollowUp} disabled={fuSaving}>
              {fuSaving ? 'Processing...' : 'Process Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 6. ACTIVITY TAB (Enhanced) ──
// ═══════════════════════════════════════════════════════════
export function ActivityTab({ deal, onRefresh }) {
  const [activities, setActivities] = useState(deal.activities || []);
  const [comments, setComments] = useState(deal.comments || []);
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);
  const [viewMode, setViewMode] = useState('all');

  const loadActivity = useCallback(async () => {
    try { const res = await API.get(`/buyer/deals/${deal.id}/activity`); setActivities(res.data.activities); } catch {}
  }, [deal.id]);
  const loadComments = useCallback(async () => {
    try { const res = await API.get(`/buyer/deals/${deal.id}/comments`); setComments(res.data.comments); } catch {}
  }, [deal.id]);
  useEffect(() => { loadActivity(); loadComments(); }, [loadActivity, loadComments]);

  const sendComment = async () => {
    if (!newComment.trim()) return;
    setSending(true);
    try {
      const res = await API.post(`/buyer/deals/${deal.id}/comments`, { message: newComment.trim() });
      setComments(prev => [...prev, res.data.comment]);
      setNewComment('');
    } catch { toast.error('Failed to send comment'); }
    finally { setSending(false); }
  };

  const getActionColor = (action) => {
    if (action?.includes('created') || action?.includes('connected')) return 'bg-blue-400';
    if (action?.includes('accepted') || action?.includes('approved') || action?.includes('activated') || action?.includes('payment') || action?.includes('payout')) return 'bg-emerald-400';
    if (action?.includes('rejected') || action?.includes('deleted') || action?.includes('cancelled')) return 'bg-red-400';
    if (action?.includes('sent') || action?.includes('submitted') || action?.includes('funded')) return 'bg-amber-400';
    if (action?.includes('counter') || action?.includes('amendment')) return 'bg-purple-400';
    if (action?.includes('draft') || action?.includes('edit')) return 'bg-slate-400';
    if (action?.includes('likeness') || action?.includes('extension') || action?.includes('follow')) return 'bg-indigo-400';
    return 'bg-slate-400';
  };

  const formatAction = (action) => (action || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  const allItems = viewMode === 'activity' ? activities.map(a => ({ ...a, _type: 'activity' }))
    : viewMode === 'comments' ? comments.map(c => ({ ...c, _type: 'comment' }))
    : [
      ...activities.map(a => ({ ...a, _type: 'activity' })),
      ...comments.map(c => ({ ...c, _type: 'comment' })),
    ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  return (
    <div data-testid="activity-tab" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex gap-0.5 bg-slate-100 rounded-lg p-0.5">
          {['all', 'activity', 'comments'].map(m => (
            <button key={m} onClick={() => setViewMode(m)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all capitalize
                ${viewMode === m ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`}>
              {m}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-slate-400">{allItems.length} items</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 p-4">
        {allItems.length === 0 ? (
          <div className="text-center py-6">
            <Activity className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-xs text-slate-400">No activity yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {allItems.map((item, i) => (
              <div key={item.id || i} className="flex items-start gap-3">
                {item._type === 'activity' ? (
                  <>
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${getActionColor(item.action)}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-700">
                        <span className="font-medium text-slate-800">{item.actor_name || 'System'}</span>{' '}
                        {formatAction(item.action).toLowerCase()}
                      </p>
                      {item.metadata_json && Object.keys(item.metadata_json).length > 0 && (
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {Object.entries(item.metadata_json).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                        </p>
                      )}
                      <p className="text-[10px] text-slate-300 mt-0.5">{new Date(item.created_at).toLocaleString()}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MessageSquare className="w-2.5 h-2.5 text-blue-600" />
                    </div>
                    <div className="flex-1 p-2 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[10px] font-semibold text-slate-700">{item.author_name}</span>
                        <span className="text-[9px] text-slate-400">{new Date(item.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-xs text-slate-600">{item.message}</p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
          <Input data-testid="comment-input" placeholder="Add a comment..." value={newComment}
            onChange={e => setNewComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendComment()}
            className="h-8 text-xs border-slate-200 flex-1" />
          <Button data-testid="send-comment-btn" onClick={sendComment} disabled={sending || !newComment.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-3">
            <Send className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
